import React, { Fragment } from 'react';
import { Nav, Collapse } from 'reactstrap';
import MediaQuery from 'react-responsive';
import Sticky from 'react-stickynode';
import { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import { ReactComponent as IconPower } from '@whitelabel/media/icons/small/power.svg';
import { ISidebarNavItem } from '../../helpers/types';
import {
  StyledSidebarCard,
  StyledSidebarTitle,
  StyledNavItem,
  StyledNavLink,
  StyledChevron,
  StyledChildNav,
  StyledChildNavItem,
  StyledHR,
  StyledItemDivider,
  StyledIcon,
  StyledLogoutButton,
  StyledWrapper,
} from './styledSidebarNav';
import messages from './messages';

interface ISidebarNavProps {
  title?: string;
  items?: ISidebarNavItem[];
  activeItem?: string;
  onSetActive?: (...args: any[]) => any;
  onClickNavLink?: (...args: any[]) => any;
  sticky?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  bottomBoundary?: string;
  showLogoutButton?: boolean;
}

const SidebarNav = ({
  title,
  items = [],
  activeItem,
  onSetActive,
  sticky,
  bottomBoundary = '#Main',
  showLogoutButton = false,
  onClickNavLink,
}: ISidebarNavProps): JSX.Element => {
  const theme = useTheme();
  const intl = useIntl();
  const handleSetActive = (key: string) => () => onSetActive?.(key);

  const card = (
    <StyledSidebarCard role="navigation">
      {title && <StyledSidebarTitle>{title}</StyledSidebarTitle>}

      <Nav vertical>
        {items?.map(({ key, name, path, items: childItems, active = activeItem === key }: any) => (
          <Fragment key={key}>
            <StyledNavItem active={active}>
              <StyledNavLink
                to={path}
                onSetActive={onSetActive && handleSetActive(key)}
                active={active}
                onClick={onClickNavLink && onClickNavLink(key)}
              >
                {name}
                {childItems && <StyledChevron />}
              </StyledNavLink>

              {childItems && (
                <Collapse isOpen={active}>
                  <StyledChildNav>
                    {childItems.map(({ key: childKey, name: childName, path: childPath }: any) => (
                      <StyledChildNavItem key={childKey}>
                        <StyledNavLink to={childPath} onSetActive={onSetActive && handleSetActive(key)}>
                          {childName}
                        </StyledNavLink>
                      </StyledChildNavItem>
                    ))}
                  </StyledChildNav>
                </Collapse>
              )}
            </StyledNavItem>

            {childItems && active && <StyledHR />}
          </Fragment>
        ))}
      </Nav>
      {showLogoutButton && (
        <StyledWrapper>
          <StyledItemDivider />
          <StyledLogoutButton to={`/${intl.locale}/logout`} data-analytics="profileLogoutLink">
            <StyledIcon as={IconPower} />
            {intl.formatMessage(messages.logoutLink)}
          </StyledLogoutButton>
        </StyledWrapper>
      )}
    </StyledSidebarCard>
  );

  return sticky ? (
    <MediaQuery minDeviceWidth={theme.gridBreakpoints[sticky]}>
      {(matches) =>
        matches ? (
          <Sticky top={16} activeClass="sticky" bottomBoundary={bottomBoundary}>
            {card}
          </Sticky>
        ) : (
          card
        )
      }
    </MediaQuery>
  ) : (
    card
  );
};

export default SidebarNav;
